/**
 * Plain-language copy for Laravel validation on merchant listing (product) APIs.
 * When no rule matches, the original API message is returned.
 */
const variantRowLabel = (fieldKey: string | undefined, message: string): string => {
  const source = `${fieldKey ?? ''} ${message}`;
  const m = source.match(/variants_matrix\.(\d+)\./i);
  if (!m) return 'A variant';
  const n = Number(m[1]);
  if (Number.isNaN(n)) return 'A variant';
  return `Variant ${n + 1}`;
};

const shouldHumanizeProductListing = (fieldKey: string | undefined, message: string): boolean => {
  const key = (fieldKey ?? '').toLowerCase();
  const msg = message.toLowerCase();
  const bundle = `${key} ${msg}`;
  if (
    bundle.includes('variants_matrix') ||
    bundle.includes('attributes.') ||
    bundle.includes('photos.') ||
    bundle.includes('variant_photos') ||
    bundle.includes('deleted_variant') ||
    bundle.includes('deleted_photos') ||
    bundle.includes('preview_key') ||
    bundle.includes('generate_variants') ||
    bundle.includes('has_variants')
  ) {
    return true;
  }
  if (
    /^(sku|title|description|price|status|category_id|subcategory_id|processing_minutes|photos)$/.test(key)
  ) {
    return true;
  }
  return false;
};

const humanizeVariantMatrixField = (
  fieldKey: string | undefined,
  message: string,
  field: 'price' | 'quantity' | 'sku' | 'is_active' | 'preview_key' | 'variant_photos' | 'id' | 'variant_id'
): string | null => {
  const row = variantRowLabel(fieldKey, message);
  const m = message.toLowerCase();

  if (field === 'price') {
    if (/must be at least ([\d.]+)/i.test(message)) {
      const amount = message.match(/must be at least ([\d.]+)/i)?.[1] ?? '0.01';
      return `${row}: Enter a price of at least $${amount}.`;
    }
    if (/must not be greater than|cannot be greater|too large|exceed/i.test(message)) {
      return `${row}: That price is too high. Use a smaller amount.`;
    }
    if (/required|must be present|can't be empty|cannot be empty/i.test(message)) {
      return `${row}: Add a price.`;
    }
    if (/numeric|number|invalid/i.test(message)) {
      return `${row}: Enter a valid price.`;
    }
    return `${row}: Check the price and try again.`;
  }

  if (field === 'quantity') {
    if (/must be at least (\d+)/i.test(message)) {
      const amount = message.match(/must be at least (\d+)/i)?.[1] ?? '0';
      return `${row}: Stock must be at least ${amount}.`;
    }
    if (/must not be greater than|exceed/i.test(message)) {
      return `${row}: That stock amount is too high.`;
    }
    if (/required|must be present|integer|numeric|number/i.test(message)) {
      return `${row}: Enter a whole number for stock.`;
    }
    return `${row}: Check the stock quantity and try again.`;
  }

  if (field === 'sku') {
    if (/already been taken|unique|exists/i.test(message)) {
      return `${row}: That SKU is already in use. Pick a different SKU.`;
    }
    if (/may not be greater than|too long|max:/i.test(message)) {
      return `${row}: SKU is too long. Shorten it and try again.`;
    }
    if (/required|must be present/i.test(message)) {
      return `${row}: Add a SKU or clear the field if your setup allows it.`;
    }
    if (/format|invalid/i.test(message)) {
      return `${row}: SKU format is not accepted. Use letters, numbers, and dashes only.`;
    }
    return `${row}: Check the SKU and try again.`;
  }

  if (field === 'is_active') {
    return `${row}: Active / inactive setting could not be saved. Toggle it and try again.`;
  }

  if (field === 'preview_key') {
    return 'We could not match a variant option. Refresh the page and try again, or contact support if it keeps happening.';
  }

  if (field === 'variant_photos' || field === 'id' || field === 'variant_id') {
    if (/upload|image|photo|file|mime|size/i.test(message)) {
      return `${row}: One of the variant images could not be saved. Try a different image or a smaller file.`;
    }
    return `${row}: Variant details could not be saved. Check images and IDs, then try again.`;
  }

  if (m.includes('failed to upload')) {
    return `${row}: An image failed to upload. Try a different file or a smaller size.`;
  }

  return null;
};

const humanizePhotosError = (fieldKey: string | undefined, message: string): string | null => {
  if (!/photos\.\d+/i.test(`${fieldKey ?? ''} ${message}`)) return null;
  if (/failed to upload|could not be uploaded|invalid/i.test(message)) {
    return 'One of your product photos could not be uploaded. Try a different image or a smaller file (under the size limit).';
  }
  if (/required|must have|at least/i.test(message)) {
    return 'Add at least one product photo, or check that each image meets the size and format rules.';
  }
  if (/may not have more than|maximum|too many/i.test(message)) {
    return 'You have more photos than allowed. Remove some photos and try again.';
  }
  return 'There is a problem with one of your product photos. Replace it or use a smaller file.';
};

const humanizeAttributesError = (fieldKey: string | undefined, message: string): string | null => {
  if (!/attributes\.\d+/i.test(`${fieldKey ?? ''} ${message}`)) return null;
  if (/name/i.test(message) && /required|must/i.test(message)) {
    return 'Each product option needs a name (for example Colour or Size).';
  }
  if (/options/i.test(message) && /required|least|one/i.test(message)) {
    return 'Each option needs at least one value (for example Red or Large).';
  }
  if (/duplicate|unique/i.test(message)) {
    return 'Option names or values are duplicated. Use unique names and values.';
  }
  return 'Check your product options (names and values) and try again.';
};

const humanizeRootProductField = (fieldKey: string | undefined, message: string): string | null => {
  const key = (fieldKey ?? '').toLowerCase();
  const m = message.toLowerCase();

  if (key === 'title') {
    if (/required/i.test(m)) return 'Add a product title.';
    if (/max|too long|greater than/i.test(m)) return 'Title is too long. Shorten it and try again.';
    return 'Check the product title and try again.';
  }
  if (key === 'description') {
    if (/required/i.test(m)) return 'Add a product description.';
    if (/max|too long/i.test(m)) return 'Description is too long. Shorten it and try again.';
    return 'Check the product description and try again.';
  }
  if (key === 'sku') {
    if (/already|taken|unique|exists/i.test(m)) return 'This SKU is already used on one of your listings. Use a different SKU.';
    if (/required/i.test(m)) return 'Add a SKU, or leave it blank if your store allows that.';
    if (/max|too long/i.test(m)) return 'SKU is too long. Shorten it and try again.';
    return 'Check the SKU and try again.';
  }
  if (key === 'price') {
    if (/at least ([\d.]+)/i.test(message)) {
      const amount = message.match(/at least ([\d.]+)/i)?.[1] ?? '0.01';
      return `Enter a price of at least $${amount}.`;
    }
    if (/required|numeric/i.test(m)) return 'Enter a valid price.';
    return 'Check the price and try again.';
  }
  if (key === 'status') {
    return 'That listing status is not allowed right now. Pick Draft or Published and try again.';
  }
  if (key === 'category_id' || key === 'category') {
    if (/required|invalid|exists/i.test(m)) return 'Choose a category and subcategory (if shown).';
    return 'Category could not be saved. Pick another category and try again.';
  }
  if (key === 'subcategory_id' || key === 'subcategory') {
    if (/required|invalid|exists/i.test(m)) return 'Choose a subcategory, or pick a different main category.';
    return 'Subcategory could not be saved. Pick another option and try again.';
  }
  if (key === 'processing_minutes') {
    if (/required|integer|between|min|max/i.test(m)) {
      return 'Choose a valid preparation time (in minutes) from the list.';
    }
    return 'Check preparation time and try again.';
  }
  if (key === 'photos') {
    return humanizePhotosError(fieldKey, message) ?? 'There is a problem with your product photos.';
  }
  return null;
};

/**
 * @param fieldKey Laravel error key when available (e.g. `variants_matrix.0.price`)
 * @param rawMessage Original validation message from the API
 */
export function humanizeListingApiError(fieldKey: string | undefined, rawMessage: string): string {
  const message = (rawMessage || '').trim();
  if (!message) return 'Something went wrong. Please try again.';

  if (!shouldHumanizeProductListing(fieldKey, message)) {
    return message;
  }

  const key = fieldKey ?? '';
  const combined = `${key} ${message}`;

  const vmField = combined.match(/variants_matrix\.\d+\.(\w+)/i)?.[1]?.toLowerCase();
  if (vmField) {
    const allowed = new Set(['price', 'quantity', 'sku', 'is_active', 'preview_key', 'variant_photos', 'id', 'variant_id']);
    if (allowed.has(vmField)) {
      const out = humanizeVariantMatrixField(
        fieldKey,
        message,
        vmField as 'price' | 'quantity' | 'sku' | 'is_active' | 'preview_key' | 'variant_photos' | 'id' | 'variant_id'
      );
      if (out) return out;
    }
  }

  const photosOut = humanizePhotosError(fieldKey, message);
  if (photosOut) return photosOut;

  const attrOut = humanizeAttributesError(fieldKey, message);
  if (attrOut) return attrOut;

  if (/deleted_variant|deleted_variants/i.test(combined)) {
    return 'We could not remove one of the variants. Refresh and try again.';
  }
  if (/deleted_photos/i.test(combined)) {
    return 'We could not remove one of the saved photos. Refresh and try again.';
  }

  const rootOut = humanizeRootProductField(fieldKey, message);
  if (rootOut) return rootOut;

  if (/generate_variants|has_variants/i.test(combined)) {
    return 'Variant settings could not be saved. Check options and variant rows, then try again.';
  }

  if (/variants_matrix/i.test(combined)) {
    return 'There is a problem with your variant rows. Check prices, stock, SKUs, and images, then try again.';
  }

  return message;
}
